import { useState, useEffect, useRef } from "react";
import axios from 'axios';
import { useNavigate } from "react-router-dom";

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function FAQItem({ faq, index, isOpen, onToggle, visible }) {
  const contentRef = useRef(null);

  return (
    <div
      className={`border border-slate-200 rounded-xl overflow-hidden transition-all duration-500 bg-white
        ${isOpen ? "shadow-md" : "shadow-sm hover:shadow-md"}
        ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}
      `}
      style={{ transitionDelay: `${index * 0.08}s` }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-5 text-left group"
      >
        <span
          className={`font-black uppercase text-sm tracking-widest transition-colors duration-300 pr-4
            ${isOpen ? "text-green-500" : "text-slate-900 group-hover:text-green-500"}
          `}
        >
          {faq.question}
        </span>
        <span
          className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300
            ${
              isOpen
                ? "bg-green-500 text-white rotate-0"
                : "bg-slate-100 text-slate-500 group-hover:bg-green-100 group-hover:text-green-500"
            }
          `}
        >
          {isOpen ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="w-4 h-4"
            >
              <path d="M5 12h14" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="w-4 h-4"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          )}
        </span>
      </button>

      {/* Animated answer */}
      <div
        className="overflow-hidden transition-all duration-400 ease-in-out"
        style={{
          maxHeight: isOpen
            ? `${contentRef.current?.scrollHeight || 200}px`
            : "0px",
          transition: "max-height 0.4s ease",
        }}
      >
        <div ref={contentRef} className="px-6 pb-5">
          <div className="h-px bg-slate-100 mb-4" />
          <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [headerRef, headerVisible] = useInView();
  const [faqRef, faqVisible] = useInView();
  const [imgRef, imgVisible] = useInView();
  const [faq,setFaq] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();


  const fetchFaq = async()=>{
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/faq/get-all-faqs`);
      setFaq( res.data.data); 
    } catch (error) {
      console.log("Error", error);
    }finally{
      setLoading(false);
    }
  }


  useEffect(() => {
    fetchFaq();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;700;800;900&family=DM+Sans:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <div
      className="flex items-center justify-center py-20 px-6 md:px-10 lg:px-12 xl:px-20 bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 relative overflow-hidden"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle,#cbd5e1 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* Glow */}
      <div className="absolute top-0 right-1/3 w-[500px] h-[400px] pointer-events-none bg-green-500/5 blur-3xl rounded-full" />

      <div className="relative w-full ">
        {/* ── Header row ── */}
        <div
          ref={headerRef}
          className={`flex flex-col sm:flex-row sm:items-start justify-between gap-6 mb-12 transition-all duration-700
            ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
          `}
        >
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px w-6 bg-green-400" />
              <span className="text-green-500 font-bold text-xs tracking-widest uppercase">
                Helpful Answers
              </span>
            </div>
            <h2
              className="font-bold uppercase text-gray-900 text-2xl md:text-4xl lg:text-[45px] font-rajdhani leading-none tracking-wider"
         
            >
              Frequently Asked Questions
              <br />
              <span
                className="text-transparent "
                style={{ WebkitTextStroke: "2px #0f1f4b" }}
              >
                About Our
              </span>{" "}
              Services
            </h2>
          </div>

          {/* Contact Us CTA */}
          <div className="flex-shrink-0 pt-1">
            <button
            onClick={()=>{
              navigate("/contact");
              window.scrollTo({
                top: 0,
                behavior : 'smooth'
              })
            }}
              className="group relative overflow-hidden flex items-center gap-3 bg-green-500 text-white font-black uppercase text-sm px-6 py-4 rounded-xl hover:shadow-xl hover:shadow-green-500/30 transition-shadow duration-300"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                letterSpacing: "0.1em",
              }}
            >
              <span className="relative z-10">Contact Us</span>
              <span className="relative z-10 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300">
                →
              </span>
              <div className="absolute inset-0 bg-gray-900 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 z-0" />
            </button>
          </div>
        </div>

        {/* ── Body: FAQ list + Image ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_620px] gap-8 items-start">
          {/* FAQ accordion */}
          <div ref={faqRef} className="space-y-3">
            {faq.map((faq, i) => (
              <FAQItem
                key={i}
                faq={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                visible={faqVisible}
              />
            ))}
          </div>

          {/* Image */}
          <div
            ref={imgRef}
            className={` rounded-2xl overflow-hidden shadow-2xl sticky top-8 transition-all duration-700 delay-300
              ${imgVisible ? "opacity-100 translate-x-0 scale-100" : "opacity-0 translate-x-10 scale-[0.97]"}
            `}
            style={{ height: "500px" }}
          >
            <img
              src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80"
              alt="Construction professionals"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />

            {/* Floating badge */}
            <div className="absolute bottom-6 left-6 right-6 rounded-xl px-5 py-4 bg-slate-900/85 backdrop-blur-sm border border-white/10 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-green-500 to-green-400">
                <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <div>
                <p
                  className="text-white font-black text-sm uppercase tracking-wide"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Still Have Questions?
                </p>
                <p className="text-blue-200/60 text-xs mt-0.5">
                  Our team is available 24/7 to help you
                </p>
              </div>
            </div>

            {/* green corner */}
            <div className="absolute top-0 right-0 w-16 h-16 rounded-bl-3xl bg-gradient-to-br from-green-500 to-green-400 opacity-90" />
            <div className="absolute top-3 right-3">
              <svg
                viewBox="0 0 24 24"
                fill="white"
                className="w-5 h-5 opacity-80"
              >
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
